import { notificationRealtime, type RealtimeHandlers } from './notification-realtime'
import { getAuthHeaders } from '@/lib/api-client'
import { env } from '@/lib/env'

// Backend API URL
const BACKEND_URL = env.backendApiUrl

/** Mirrors `NotificationType` in backend/app/schemas/notifications.py, minus the retired `app_request`. */
export type NotificationType =
  | 'access_request'
  | 'app_assigned'
  | 'app_removed'
  | 'knowledge_area_assigned'
  | 'knowledge_area_removed'
  | 'profile_updated'
  | 'user_approved'
  | 'user_rejected'
  | 'document_upload_completed'
  | 'document_upload_failed'
  | 'session_assigned'
  | 'review_tasks_created'
  | 'prompt_shared'
  | 'new_bug_report'
  | 'new_feature_request'
  | 'routine_completed'
  | 'routine_failed'
  | 'document_processing_completed'
  | 'document_processing_failed'
  | 'recording_transcription_completed'
  | 'recording_transcription_failed'
  | 'quota_request'
  | 'quota_request_decided'
  | 'bug_report_reply'
  | 'bug_report_status_changed'
  // #1168: an admin reclassified the reporter's ticket (bug <-> feature).
  | 'bug_report_type_changed'

// Payload contract v2 (#1089): optional, well-known keys inside `data`. The
// rest of `data` stays free JSON. Read them through
// `lib/utils/notificationPayload.ts`, which drops malformed values.

export type NotificationCategory = 'bug_reports' | 'documents' | 'routines' | 'requests' | 'shared'

export type NotificationOutcome = 'success' | 'failure' | 'info' | 'action_required'

/** What a click opens: `openWindow(app, undefined, undefined, params)`. Params use long keys. */
export interface NotificationTarget {
  app: string
  params?: Record<string, string | number | boolean>
}

export interface NotificationActor {
  user_id?: string
  name?: string
}

export interface NotificationPayloadV2 {
  target?: NotificationTarget
  /** Bundles notifications about one thing, e.g. `bug_report:<id>`. */
  group_key?: string
  category?: NotificationCategory
  outcome?: NotificationOutcome
  actor?: NotificationActor
}

export interface Notification {
  id: string
  user_id: string
  type: NotificationType
  title: string
  message: string
  data: NotificationPayloadV2 & Record<string, unknown>
  is_read: boolean
  created_at: string
  read_at?: string
}

export interface NotificationFilters {
  is_read?: boolean
  type?: string
  limit?: number
  offset?: number
}

export class NotificationService {
  /**
   * Lädt alle Notifications für den aktuellen Benutzer (via Backend API)
   */
  static async getNotifications(filters: NotificationFilters = {}): Promise<Notification[]> {
    try {
      const params = new URLSearchParams()
      if (filters.is_read !== undefined) {
        params.append('is_read', String(filters.is_read))
      }
      if (filters.type) {
        params.append('type', filters.type)
      }
      if (filters.limit) {
        params.append('limit', String(filters.limit))
      }
      if (filters.offset) {
        params.append('offset', String(filters.offset))
      }

      const url = `${BACKEND_URL}/api/v1/notifications${params.toString() ? '?' + params.toString() : ''}`
      const headers = getAuthHeaders()
      const response = await fetch(url, {
        credentials: 'include',
        method: 'GET',
        headers
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }))
        console.error('[NotificationService] getNotifications - error:', error)
        throw new Error(error.detail || 'Failed to load notifications')
      }

      const data = await response.json()
      return data.notifications || []
    } catch (error) {
      console.error('[NotificationService] Error loading notifications:', error)
      throw error
    }
  }

  /**
   * Markiert eine einzelne Notification als gelesen (via Backend API)
   */
  static async markAsRead(notificationId: string): Promise<void> {
    try {
      const url = `${BACKEND_URL}/api/v1/notifications/${notificationId}/read`
      const response = await fetch(url, {
        credentials: 'include',
        method: 'PUT',
        headers: getAuthHeaders()
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }))
        console.error('[NotificationService] markAsRead - error:', error)
        throw new Error(error.detail || 'Failed to mark notification as read')
      }
    } catch (error) {
      console.error('[NotificationService] Error marking as read:', error)
      throw error
    }
  }

  /**
   * Marks a single notification as unread again (#1135, via Backend API)
   */
  static async markAsUnread(notificationId: string): Promise<void> {
    const response = await fetch(`${BACKEND_URL}/api/v1/notifications/${notificationId}/unread`, {
      credentials: 'include',
      method: 'PUT',
      headers: getAuthHeaders()
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to mark notification as unread')
    }
  }

  /**
   * Markiert alle Notifications als gelesen (via Backend API)
   */
  static async markAllAsRead(): Promise<void> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/notifications/mark-all-read`, {
        credentials: 'include',
        method: 'PUT',
        headers: getAuthHeaders()
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(error.detail || 'Failed to mark all notifications as read')
      }
    } catch (error) {
      console.error('[NotificationService] Error marking all as read:', error)
      throw error
    }
  }

  /**
   * Löscht eine einzelne Notification (via Backend API)
   */
  static async deleteNotification(notificationId: string): Promise<void> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/notifications/${notificationId}`, {
        credentials: 'include',
        method: 'DELETE',
        headers: getAuthHeaders()
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(error.detail || 'Failed to delete notification')
      }
    } catch (error) {
      console.error('[NotificationService] Error deleting notification:', error)
      throw error
    }
  }

  /**
   * Löscht alle gelesenen Notifications (via Backend API)
   */
  static async deleteAllRead(): Promise<void> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/notifications`, {
        credentials: 'include',
        method: 'DELETE',
        headers: getAuthHeaders()
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(error.detail || 'Failed to delete read notifications')
      }
    } catch (error) {
      console.error('[NotificationService] Error deleting read notifications:', error)
      throw error
    }
  }

  /**
   * Gibt die Anzahl ungelesener Notifications zurück (via Backend API)
   */
  static async getUnreadCount(): Promise<number> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/notifications`, {
        credentials: 'include',
        method: 'GET',
        headers: getAuthHeaders()
      })

      if (!response.ok) {
        return 0
      }

      const data = await response.json()
      return data.unread_count || 0
    } catch (error) {
      console.error('[NotificationService] Error getting unread count:', error)
      return 0
    }
  }

  /**
   * Re-sync after a realtime (re)connect (#1090): the notifications created
   * after `since` (the newest `created_at` the caller holds, passed back
   * verbatim so no precision is lost) plus the person's whole unread count.
   */
  static async getNotificationsSince(
    since: string,
    limit = 50
  ): Promise<{ notifications: Notification[]; unreadCount: number }> {
    const params = new URLSearchParams({ since, limit: String(limit) })
    const response = await fetch(`${BACKEND_URL}/api/v1/notifications?${params.toString()}`, {
      credentials: 'include',
      method: 'GET',
      headers: getAuthHeaders()
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }))
      throw new Error(error.detail || 'Failed to re-sync notifications')
    }
    const data = await response.json()
    return { notifications: data.notifications || [], unreadCount: data.unread_count || 0 }
  }

  /**
   * Realtime updates for the signed-in user over Web PubSub. `onConnected`
   * fires on every (re)connect — re-sync there. Connection failures are
   * retried in the background.
   */
  static async subscribeToNotifications(handlers: RealtimeHandlers): Promise<string> {
    return notificationRealtime.subscribe(handlers)
  }

  static async unsubscribeFromNotifications(subscriptionId: string | null): Promise<void> {
    if (!subscriptionId) return
    await notificationRealtime.unsubscribe(subscriptionId)
  }

  /**
   * Erstellt eine Notification über die Backend-API.
   * cdb_tqs speichert sie, das Backend pusht sie an die offenen Sitzungen des Empfängers.
   */
  static async createNotification(
    userId: string,
    type: Notification['type'],
    title: string,
    message: string,
    data: NotificationPayloadV2 & Record<string, unknown> = {}
  ): Promise<Notification> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/notifications`, {
        credentials: 'include',
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_id: userId,
          type,
          title,
          message,
          data,
        }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(error.detail || 'Failed to create notification')
      }

      return await response.json()
    } catch (error) {
      console.error('[NotificationService] Error creating notification:', error)
      throw error
    }
  }
}
